<template>
    <div>
        <b-button variant="outline-info" class="float-right" v-show="projectPermissions.includes('CREATE_INVITATION')" @click="inviteMember">{{ $t('project.settings.participants.invite') }}</b-button>
        <br><br><br>
        <table class="table table-hover">
            <tbody>
                <tr v-for="member in projectMembers" :key="member.id">
                    <td>
                        <div class="text-info font-weight-bold">{{ member.displayName }}</div>
                        <div class="text-muted small">{{ member.email }}</div>
                    </td>
                    <td>
                        <b-button
                            size="sm"
                            variant="outline-danger"
                            class="float-left pb-0"
                            @click.stop="leaveProject(member)"
                            v-show="member.id === userData.id && projectPermissions.includes('EXIT_PROJECT_ROOM')"
                            :title="$t('project.settings.participants.leaveProject')">
                            <icon name="times"></icon>
                        </b-button>
                    </td>
                    <td>
                        <span class="badge badge-info p-2 mr-1" v-show="member.id === userData.id">{{ $t('project.settings.participants.itsYou') }}</span>
                        <span class="badge badge-secondary p-2 " >  {{['OWNER', 'GUEST', 'MEMBER'].indexOf(member.membership) >= 0 ?$t('project.settings.participants.roles.' + member.membership.toLowerCase()) : ( ( typeof member.membership === "string")? member.membership: findRoleById(member.membership)) }}  </span>
                    </td>
                    <td>
                        <b-button
                            size="sm"
                            variant="outline-danger"
                            class="float-right pb-0"
                            @click.stop="deleteMember(member)"
                            :disabled="member.id === userData.id || !projectPermissions.includes('DELETE_MEMBERSHIP')"
                            :title="$t('project.settings.participants.removeMember')">
                            <icon name="trash"></icon>
                        </b-button>
                        <b-button
                            size="sm"
                            variant="outline-info"
                            class="float-right mr-1 pb-0"
                            @click.stop="changeMemberRole(member)"
                            :disabled="member.id === userData.id || !projectPermissions.includes('CHANGE_PROJECT_MEMBER_ROLE')"
                            :title="$t('project.settings.participants.changeRole')">
                            <icon name="users"></icon>
                        </b-button>
                    </td>
                </tr>
            </tbody>
        </table>
        
        <hr><br>
        <span class="text-info font-weight-bold">{{ $t('project.settings.invitations.title') }}</span>
        <hr v-show="projectInvitations.length === 0">

        <v-wait for="invitationsListSpinner">
            <template slot="waiting">
                <div class="mx-auto" style="width: 100px;">
                    <hollow-dots-spinner
                        slot='waiting'
                        :animation-duration="1500"
                        :dot-size="15"
                        :dots-num="3"
                        :color="'blue'"
                    />
                </div>
            </template>
            <span class="small" v-show="projectInvitations.length === 0">{{ $t('project.settings.invitations.text.noInvitations') }}</span>
        </v-wait>

        <table class="table table-hover">
            <tbody>
                <tr v-for="invitation in projectInvitations" :key="invitation.token">
                    <td>
                        <div class="text-info font-weight-bold">{{ invitation.email }}</div>
                        <div class="text-muted small">{{ $t('project.settings.invitations.text.created') }} {{ invitation.created | formatDateTimeNoSec }}</div>
                        <div class="text-muted small">{{ $t('project.settings.invitations.text.expires') }} {{ invitation.expires | formatDateTimeNoSec }}</div>
                    </td>
                    <td class="text-right">
                        <span class="badge badge-info p-2" v-show="!isInvitationExpired(invitation)">{{ $t('project.settings.invitations.status.active') }}</span>
                        <span class="badge badge-danger p-2" v-show="isInvitationExpired(invitation)">{{ $t('project.settings.invitations.status.expired') }}</span>
                        <b-button
                            size="sm"
                            variant="outline-danger"
                            class="ml-1 pb-1"
                            @click.stop="deleteInvitation(invitation.token)"
                            :title="$t('project.settings.invitations.text.revokeInvitation')"
                            v-show="projectPermissions.includes('DELETE_INVITATION')">
                            <icon name="trash"></icon>
                        </b-button>
                    </td>
                </tr>
            </tbody>
        </table>

        <dropdown-dialog
            dialog-id="dlgDropdown_ProjectSettings"
            :dialog-title="$t('project.settings.participants.changeRole')"
            :field-label="$t('project.settings.participants.rolesLabel') + ':'"
            :source-data="dropdownDialog.sourceData"
            :valueToPreselect="dropdownDialog.valueToPreselect"
            :context="dropdownDialog.context">
        </dropdown-dialog>
        
        <invitation-dialog :successCallback="loadInvitations"></invitation-dialog>
    </div>
</template>

<script>

import toastr from 'toastr';
import moment from 'moment';
import { mapGetters } from 'vuex';
import i18n from '@/i18n.bootstrap';
import systemApi from '../../../api/system';
import projectsApi from '../../../api/projects';
import InvitationDialog from './InvitationDialog';
import DropdownDialog from '../../Shared/DropdownDialog';

export default {
    name: 'tab-members',

    components: { DropdownDialog, InvitationDialog },

    data() {
        return {
            dropdownDialog: {
                sourceData: [],
                valueToPreselect: null,
                context: {}
            },

            projectInvitations: []
        }
    },

    mounted() {
        this.$root.$on('dlgDropdown_ProjectSettings::value-selected', (payload) => {
            this.updateMemberRole(payload.context.member, payload.selectedValue);
        });

        this.loadInvitations();
    },
    created() {
      this.$store.dispatch('system/loadRoles').then(result => {
        this.dropdownDialog.sourceData = result.map(role => ({text: role.permanent? i18n.t('project.settings.participants.roles.' + role.name.toLowerCase()) : role.name, value: role.id}));
      });
    },
    computed: {
        ...mapGetters({
            userData: 'userData/userData',
            isProjectOwner: 'projects/isProjectOwner',
            projectMembers: 'projects/projectCollaborators',
            roles: 'system/roles',
        }),
        projectPermissions() {
          if(this.$store.state.projects.currentProject.projectData.role){
            return this.$store.state.projects.currentProject.projectData.role.permissions;
          } else {
            return [];
          }
        },
    },

    methods: {
        findRoleById(id) {
          const role = this.roles.find(r => { return r.id === id});
          return ['OWNER', 'GUEST', 'MEMBER'].indexOf(role.name) >= 0? i18n.t('project.settings.participants.roles.' + role.name.toLowerCase()) :role.name;
        },
        loadInvitations() {
            this.$wait.start('invitationsListSpinner');

            projectsApi.loadInvitationsByProject(this.$route.params.projectId)
                .then(response => {
                    if (response.data) {
                        this.$wait.end('invitationsListSpinner');
                        this.projectInvitations = response.data;
                    }
                })
                .catch(() => {
                    this.$wait.end('invitationsListSpinner');
                });
        },

        inviteMember() {
            this.$root.$emit('bv::show::modal', 'dlgInviteMember');

        },

        isInvitationExpired(invitation) {
            return (moment() > moment(invitation.expires));
        },

        changeMemberRole(member) {
            this.dropdownDialog.valueToPreselect = member.membership;
            this.dropdownDialog.context.member = member;
            this.$root.$emit('bv::show::modal', 'dlgDropdown_ProjectSettings');

        },

        leaveProject(member) {
            const leaveProjectAction = () => {
                let otherOwnerPresent = false;
                let otherOwner = this.projectMembers.find(m => m.membership === 'OWNER' && m.id !== this.userData.id);
                if (otherOwner) {
                    otherOwnerPresent = true;
                }
                
                let canLeave = (member.membership !== 'OWNER') || (member.membership === 'OWNER' && otherOwnerPresent);
                
                if (canLeave) {
                    this.$store.dispatch('projects/removeMemberFromProject', {
                        projectId: this.$route.params.projectId,
                        userId: this.userData.id
                    }).then(() => {
                        // TODO: logout user
                        this.$router.push('/');
                    });
                } else {
                    toastr.error(i18n.t('project.settings.participants.action.lastOwner'));
                }
            };

            this.$snotify.confirm(i18n.t('general.promptQuestion'), '', {
                closeOnClick: true,
                backdrop: 0.3,
                buttons: [
                    { text: i18n.t('general.yes'), action: leaveProjectAction },
                    { text: i18n.t('general.no') }
                ]
            });
        },

        deleteMember(member) {
            const deleteTaskAction = () => {
                this.$store.dispatch('projects/removeMemberFromProject', {
                    projectId: this.$route.params.projectId,
                    userId: member.id
                });
            };

            this.$snotify.confirm(i18n.t('general.promptQuestion'), '', {
                closeOnClick: true,
                backdrop: 0.3,
                buttons: [
                    { text: i18n.t('general.yes'), action: deleteTaskAction },
                    { text: i18n.t('general.no') }
                ]
            });
        },

        deleteInvitation(token) {
            const revokeInvitation = () => {
                systemApi.revokeInvitation(token)
                    .then(() => this.loadInvitations())
                    .catch(() => toastr.error(i18n.t('project.settings.invitations.errorRevoking')));
            };

            this.$snotify.confirm(i18n.t('general.promptQuestion'), '', {
                closeOnClick: true,
                backdrop: 0.3,
                buttons: [
                    { text: i18n.t('general.yes'), action: revokeInvitation },
                    { text: i18n.t('general.no') }
                ]
            });
        },

        updateMemberRole(member, newRole) {
            let memberStatus = {
                [member.id]: newRole
            };

            this.$store.dispatch('projects/updateCollaboratorStatus', {
                projectId: this.$route.params.projectId,
                collaboratorStatus: memberStatus
            }).then(() => {
                member.membership = newRole;
                this.$root.$emit('bv::hide::modal', 'dlgDropdown_ProjectSettings');
            }).catch(() => {
                toastr.error(i18n.t('project.settings.participants.action.errorChangeRole'));
            });
        }
    },

    beforeDestroy() {
        this.$root.$off('dlgDropdown_ProjectSettings::value-selected');
    }
}
</script>

<style>
</style>
